import { cn } from "@/lib/utils";

interface MicVolumeMeterProps {
  /** Smoothed level in [0, 1]. */
  level: number;
  /** Number of bars to render. Defaults to 9. */
  barCount?: number;
  className?: string;
}

/**
 * Presentational mic volume meter.
 * 9 vertical bars whose heights respond to `level`, with a center-weighted
 * stagger so it reads as a meter. Adds a subtle teal glow when active.
 *
 * Pure CSS transitions — no animation library. Uses brand teal.
 */
export default function MicVolumeMeter({
  level,
  barCount = 9,
  className,
}: MicVolumeMeterProps) {
  const clamped = Math.max(0, Math.min(1, level));
  const center = (barCount - 1) / 2;
  const active = clamped > 0.05;

  return (
    <div
      className={cn("flex items-center justify-center gap-[2px] h-8 w-32", className)}
      aria-hidden="true"
      role="presentation"
    >
      {Array.from({ length: barCount }).map((_, i) => {
        // Center bars react more strongly than edge bars.
        const distanceFromCenter = Math.abs(i - center) / center; // 0..1
        const weight = 1 - distanceFromCenter * 0.55; // edges ~0.45, center 1.0
        const heightPct = Math.max(8, Math.min(100, clamped * 100 * weight));
        return (
          <span
            key={i}
            className={cn(
              "w-1 rounded-full bg-[hsl(170_85%_32%)] transition-[height,box-shadow] duration-75 ease-out",
              active && "shadow-[0_0_6px_hsl(170_85%_45%/0.7)]"
            )}
            style={{ height: `${heightPct}%` }}
          />
        );
      })}
    </div>
  );
}
